/**
 * 절차적 스튜디오 환경맵.
 *
 * drei의 `<Environment preset>`은 예쁘지만 HDR을 네트워크에서 받아온다.
 * 이 앱은 "전부 브라우저 안에서"가 약속이므로, 조명도 직접 만든다.
 * 그라데이션 돔 + 소프트박스 세 장을 PMREM에 구우면 금속과 슬라임에
 * 필요한 반사가 나온다. 파일 0바이트, 첫 프레임에 준비 완료.
 */

import { useEffect, useState } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

const DOME_VERT = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = normalize(position);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const DOME_FRAG = /* glsl */ `
varying vec3 vDir;
uniform vec3 uSky;
uniform vec3 uHorizon;
uniform vec3 uGround;
void main() {
  float h = normalize(vDir).y;
  vec3 c = h > 0.0
    ? mix(uHorizon, uSky, pow(h, 0.6))
    : mix(uHorizon, uGround, pow(-h, 0.5));
  gl_FragColor = vec4(c, 1.0);
}
`;

function softbox(color: number, intensity: number, size: number, position: [number, number, number]) {
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(size, size),
    new THREE.MeshBasicMaterial({
      color: new THREE.Color(color).multiplyScalar(intensity),
      side: THREE.DoubleSide,
      toneMapped: false,
    }),
  );
  mesh.position.set(...position);
  mesh.lookAt(0, 0, 0);
  return mesh;
}

function buildStudioScene(): THREE.Scene {
  const scene = new THREE.Scene();

  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(40, 32, 20),
    new THREE.ShaderMaterial({
      vertexShader: DOME_VERT,
      fragmentShader: DOME_FRAG,
      side: THREE.BackSide,
      depthWrite: false,
      toneMapped: false,
      uniforms: {
        // 무채색. 파란 돔은 예뻤지만 albedo 를 파랗게 물들였다 — 원본 색 그대로가 우선이다.
        uSky: { value: new THREE.Color(0x3a3d44) },
        uHorizon: { value: new THREE.Color(0x1c1e23) },
        uGround: { value: new THREE.Color(0x0b0c0f) },
      },
    }),
  );
  scene.add(dome);

  // 키 — 위 왼쪽 앞. 차가운 백색.
  scene.add(softbox(0xffffff, 5.0, 14, [-9, 11, 9]));
  // 필 — 오른쪽 아래. 아주 살짝만 따뜻하게(색온도 느낌만).
  scene.add(softbox(0xfff4ea, 1.5, 16, [12, -2, 7]));
  // 림 — 뒤쪽. 실루엣을 떼어 놓는다. 무채색.
  scene.add(softbox(0xffffff, 2.6, 12, [4, 6, -13]));

  return scene;
}

function disposeScene(scene: THREE.Scene) {
  scene.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry.dispose();
    const mat = mesh.material;
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
    else mat.dispose();
  });
}

/** PMREM으로 구운 환경 텍스처. 렌더러가 바뀌면 다시 굽는다. */
export function useStudioEnvironment(): THREE.Texture | null {
  const gl = useThree((s) => s.gl);
  const [texture, setTexture] = useState<THREE.Texture | null>(null);

  useEffect(() => {
    const pmrem = new THREE.PMREMGenerator(gl);
    pmrem.compileEquirectangularShader();
    const scene = buildStudioScene();
    const target = pmrem.fromScene(scene, 0.03);
    setTexture(target.texture);

    return () => {
      target.dispose();
      pmrem.dispose();
      disposeScene(scene);
      setTexture(null);
    };
  }, [gl]);

  return texture;
}
